import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import PageHeading from './components/PageHeading/PageHeading';
import SearchSection, { SearchControls } from './components/SearchSection/SearchSection';
import ResultsSection from './components/ResultsSection/ResultsSection';
import Sidebar from './components/Sidebar/Sidebar';
import { searchProspects, Result } from '../../services/serpApi';
import { mapResultToLeadInsert, saveLead } from '../../services/leadsService';
import { startBulkSend, validateLeadsForBulkSend } from '../../services/bulkSendService';
import { fetchAgentConfig } from '../../services/aiConfigService';
import { verifyPhoneNumbers, VerificationResult } from '../../services/whatsappVerificationService'; // Novo import
import { normalizePhoneNumber } from '../../utils/ddiUtils'; // Import DDI utilities
import { fetchCredential } from '../../services/credentialsService'; // Importando fetchCredential
import { useAuth } from '../../hooks/useAuth';
import { useSearchPersistence } from '../../hooks/useSearchPersistence';
import { useBulkSendConfig } from '../../hooks/useBulkSendConfig';
import toast from 'react-hot-toast';
import ConfirmationModal from '../../components/ConfirmationModal/ConfirmationModal';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../lib/supabase';

interface SearchContext {
  query: string;
  countryCode: string;
  controls: SearchControls;
}

const Dashboard: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const {
    searchContext,
    searchResults, // Paginated view
    filteredChunkResults, // Novo: Chunk completo filtrado
    currentChunkResults, // Full chunk view (bruto)
    totalResults,
    displayPage,
    resultsPerPage,
    chunkPage,
    hasMoreChunks,
    totalDisplayPages, // <-- Adicionado aqui
    updateSearchState,
    clearSearchState
  } = useSearchPersistence();

  const { intervalConfig, updateIntervalConfig } = useBulkSendConfig();

  const [selectedResults, setSelectedResults] = useState<number[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false); // Novo estado para verificação
  const [isWhatsappConnected, setIsWhatsappConnected] = useState(false); // Novo estado de conexão
  const [isNewSearchModalOpen, setIsNewSearchModalOpen] = useState(false);
  const [pendingSearch, setPendingSearch] = useState<{ query: string, countryCode: string, controls: SearchControls } | null>(null);

  // NEW STATES FOR PROSPECTED WARNING
  const [isProspectedWarningModalOpen, setIsProspectedWarningModalOpen] = useState(false);
  const [prospectedLeadsCount, setProspectedLeadsCount] = useState(0);
  const [isValidationWarningModalOpen, setIsValidationWarningModalOpen] = useState(false);
  const [validationInfo, setValidationInfo] = useState({ pendingCount: 0, invalidCount: 0, validCount: 0 });
  // END NEW STATES

  // Carregar status do WhatsApp ao montar
  useEffect(() => {
    const checkWhatsappStatus = async () => {
      try {
        const creds = await fetchCredential('whatsapp');
        // Verifica se a credencial existe e está ativa
        setIsWhatsappConnected(!!creds && !!creds.api_key && creds.is_active);
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Failed to check WhatsApp status:', error);
        }
        setIsWhatsappConnected(false);
      }
    };
    checkWhatsappStatus();
  }, []);


  // Function to fetch results for the current context and chunk
  const fetchChunk = useCallback(async (context: SearchContext, chunk: number) => {
    setIsLoading(true);
    setSelectedResults([]);

    const startTime = Date.now();
    let searchStatus: 'success' | 'error' | 'no_results' = 'success';
    let resultsCount = 0;
    let errorMessage: string = '';

    try {
      const data = await searchProspects({
        searchQuery: context.query,
        countryCode: context.countryCode,
        page: chunk, // Enviando o número do chunk
      });

      resultsCount = data.results.length;

      if (data.results.length === 0 && chunk === 1) {
        searchStatus = 'no_results';
        toast(t('dashboard.no_results_title'), { icon: '🔍' });
      } else if (chunk === 1 && data.total > 0) {
        // Notificação de sucesso usando o total geral
        toast.success(t('Busca concluída! {{total}} resultados encontrados.', { total: data.total }));
      }

      // Atualiza o estado persistido
      updateSearchState({
        results: data.results, // Chunk completo
        totalResults: data.total,
        chunkPage: chunk,
        displayPage: 1, // Sempre volta para a primeira página de exibição do novo chunk
        context: context,
        hasMoreChunks: data.hasNextPage,
      });

    } catch (error) {
      searchStatus = 'error';
      const rawErrorMessage = error instanceof Error ? error.message : t('errors.unknown_search_error');

      if (rawErrorMessage.includes('Chave SerpApi não configurada')) {
        errorMessage = t('errors.serpapi_not_configured');
      } else if (rawErrorMessage.includes('Sessão expirada')) {
        errorMessage = t('errors.session_expired');
      } else {
        errorMessage = rawErrorMessage;
      }

      toast.error(errorMessage);
      clearSearchState();
    } finally {
      const duration = Date.now() - startTime;
      setIsLoading(false);

      // Registrar pesquisa no histórico (apenas no chunk 1)
      if (chunk === 1 && user) {
        try {
          await supabase.from('search_history').insert({
            user_id: user.id,
            search_query: context.query,
            search_type: 'google_maps',
            location: context.countryCode,
            results_count: resultsCount,
            search_duration_ms: duration,
            api_provider: 'serpapi',
            api_credits_used: 1,
            status: searchStatus,
            error_message: errorMessage || undefined,
          });
        } catch {
          // Erro no logging não deve afetar o fluxo
        }
      }
    }
  }, [updateSearchState, clearSearchState, t, user]);

  // Effect para re-executar a busca se o contexto mudar (ex: resultsPerPage)
  useEffect(() => {
    // Este useEffect é principalmente para garantir que o estado seja carregado se o usuário navegar para longe e voltar.
    // Se o contexto existe, mas o chunk está vazio, e não estamos carregando, refetch o chunk 1.
    if (searchContext && currentChunkResults.length === 0 && !isLoading) {
      // Não chamamos fetchChunk aqui para evitar loops, pois a busca é iniciada por handleSearch.
      // Apenas garantimos que o estado inicial seja carregado se houver um contexto.
    }
  }, [searchContext, currentChunkResults.length, isLoading, fetchChunk]);


  const handleSearch = (query: string, countryCode: string, controls: SearchControls) => {
    if (!query) {
      toast.error(t('Por favor, insira um termo de busca.'));
      return;
    }

    const newContext: SearchContext = { query, countryCode, controls };

    // 1. Se o contexto de busca mudou E já temos resultados, abre o modal de confirmação
    if (searchContext && currentChunkResults.length > 0 && JSON.stringify(newContext) !== JSON.stringify(searchContext)) {
      setPendingSearch(newContext);
      setIsNewSearchModalOpen(true);
      return;
    }

    // 2. Se o contexto é novo ou não há resultados, executa a busca
    fetchChunk(newContext, 1);
  };

  const handleConfirmNewSearch = () => {
    if (pendingSearch) {
      fetchChunk(pendingSearch, 1);
      setPendingSearch(null);
    }
    setIsNewSearchModalOpen(false);
  };

  const handlePageChange = (page: number) => {
    if (!searchContext) return;

    // 1. Se a nova página está dentro do chunk atual (1 a totalDisplayPages)
    if (page <= totalDisplayPages) {
      updateSearchState({ displayPage: page });
    }
    // 2. Se a nova página é a próxima página do chunk (page == totalDisplayPages + 1) E há mais chunks
    else if (page === totalDisplayPages + 1 && hasMoreChunks) {
      const nextChunk = chunkPage + 1;
      // Ao buscar um novo chunk, o displayPage será resetado para 1 dentro do fetchChunk
      fetchChunk(searchContext, nextChunk);
    }
    // Caso contrário, estamos no final dos resultados ou a requisição é inválida.
  };

  const handleDeleteResults = (idsToDelete: number[]) => {
    // Filtra o chunk completo
    const newChunkResults = currentChunkResults.filter(result => !idsToDelete.includes(result.id));

    // Calcula o novo total de páginas de exibição
    const newTotalDisplayPages = Math.ceil(newChunkResults.length / resultsPerPage);

    // Ajusta a página de exibição se ela for maior que o novo total
    const newDisplayPage = Math.min(displayPage, Math.max(1, newTotalDisplayPages));

    // Atualiza o estado persistido
    updateSearchState({
      results: newChunkResults,
      totalResults: totalResults - idsToDelete.length,
      displayPage: newDisplayPage, // Garante que a página seja válida
    });

    setSelectedResults([]);
    toast.success(t('{{count}} resultado(s) excluído(s).', { count: idsToDelete.length }));
  };

  // Handler para exclusão individual (usado pela ResultsTableRow)
  const handleDeleteIndividualResult = (id: number) => {
    handleDeleteResults([id]);
  };

  const handleSaveSelectedResults = async () => {
    if (selectedResults.length === 0) return;

    // Usa o chunk completo para encontrar os resultados selecionados
    const leadsToSave = currentChunkResults
      .filter(result => selectedResults.includes(result.id))
      .map(result => mapResultToLeadInsert(result, searchContext?.countryCode));

    const savePromises = leadsToSave.map(lead => saveLead(lead));

    const results = await Promise.allSettled(savePromises);

    const successfulSaves = results.filter(r => r.status === 'fulfilled').length;
    const failedSaves = results.filter(r => r.status === 'rejected').length;

    if (successfulSaves > 0) {
      toast.success(t('{{count}} lead(s) salvo(s) com sucesso!', { count: successfulSaves }));
    }
    if (failedSaves > 0) {
      toast.error(t('{{count}} lead(s) falharam ao salvar. Verifique se já existem.', { count: failedSaves }));
    }

    // Limpa a seleção após a tentativa de salvar
    setSelectedResults([]);
  };

  // --- Lógica de Verificação de WhatsApp ---
  const handleVerifySelectedNumbers = async () => {
    if (!isWhatsappConnected) {
      toast.error(t('Por favor, conecte sua instância do WhatsApp na aba "Integrações" para usar o verificador.'));
      return;
    }

    if (selectedResults.length === 0) {
      toast.error(t('Selecione ao menos um lead para verificar o WhatsApp.'));
      return;
    }

    setIsVerifying(true);

    // 1. Filtrar leads selecionados que têm telefone e não estão 'Validado'
    const leadsToVerify = currentChunkResults
      .filter(result => selectedResults.includes(result.id))
      .filter(result => result.phone && result.status !== 'Validado');

    if (leadsToVerify.length === 0) {
      toast(t('Nenhum lead selecionado precisa de verificação (ou não possui telefone).'), { icon: '✅' });
      setIsVerifying(false);
      return;
    }

    const phoneNumbers = leadsToVerify.map(lead => lead.phone!).filter(Boolean) as string[];

    if (phoneNumbers.length === 0) {
      toast.error(t('Nenhum número de telefone válido encontrado nos leads selecionados.'));
      setIsVerifying(false);
      return;
    }

    // Normalize phone numbers with DDI if countryCode is available
    // AND create a mapping to the original lead index for later comparison
    const phoneToLeadIndexMap = new Map<string, number>();

    const normalizedPhoneNumbers = searchContext?.countryCode
      ? phoneNumbers.map((phone, index) => {
        const normalized = normalizePhoneNumber(phone, searchContext.countryCode);
        // Remove + sign before sending to API
        const finalNumber = normalized ? normalized.replace(/^\+/, '') : phone.replace(/\D/g, '');
        phoneToLeadIndexMap.set(finalNumber, index);
        return finalNumber;
      })
      : phoneNumbers.map((phone, index) => {
        const finalNumber = phone.replace(/\D/g, '');
        phoneToLeadIndexMap.set(finalNumber, index);
        return finalNumber;
      });

    if (process.env.NODE_ENV === 'development') {
      console.log('[WhatsApp Verification] Normalized phone numbers (without +):', normalizedPhoneNumbers);
      console.log('[WhatsApp Verification] Phone to Lead Index Map:', Array.from(phoneToLeadIndexMap.entries()));
    }

    const toastId = toast.loading(t('Verificando {{count}} número(s) de WhatsApp...', { count: normalizedPhoneNumbers.length }));

    try {
      // 2. Chamar o serviço de verificação (esperando o objeto VerificationResult diretamente)
      const verificationResults: VerificationResult = await verifyPhoneNumbers(normalizedPhoneNumbers);
      if (process.env.NODE_ENV === 'development') {
        console.log('[WhatsApp Verification] API Response:', verificationResults);
      }

      let verifiedCount = 0;

      // 3. Atualizar o estado dos leads no chunk
      // Agora usamos o mapa para fazer a correspondência correta
      const updatedResults = currentChunkResults.map((lead, leadIndex) => {
        if (!lead.phone) return lead;

        // Procurar qual número normalizado corresponde a este lead
        const leadIndexInVerificationArray = leadsToVerify.findIndex(l => l.id === lead.id);
        if (leadIndexInVerificationArray === -1) return lead; // Lead não estava na verificação

        // Encontrar o número normalizado que foi enviado para este lead
        let sentNumber: string | undefined;
        for (const [normalizedPhone, idx] of Array.from(phoneToLeadIndexMap.entries())) {
          if (idx === leadIndexInVerificationArray) {
            sentNumber = normalizedPhone;
            break;
          }
        }

        if (!sentNumber) return lead;

        // Procurar o resultado da API usando o número que foi enviado
        const hasWhatsapp = verificationResults[sentNumber];

        if (hasWhatsapp !== undefined) {
          if (hasWhatsapp && lead.status !== 'Validado') {
            verifiedCount++;
            return {
              ...lead,
              status: 'Validado' as const,
              whatsapp: true
            };
          } else if (!hasWhatsapp && lead.status !== 'Inválido') {
            return {
              ...lead,
              status: 'Inválido' as const,
              whatsapp: false
            };
          }
        }

        return lead;
      });

      // 4. Atualizar o estado persistido com os novos resultados
      updateSearchState({ results: updatedResults });
      // Mantemos a seleção: setSelectedResults([]); // REMOVIDO

      toast.success(t('Verificação concluída! {{count}} número(s) atualizado(s) para Validado.', { count: verifiedCount }), { id: toastId });

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : t('Erro ao verificar números.');
      toast.error(errorMessage, { id: toastId });
    } finally {
      setIsVerifying(false);
    }
  };
  // --- Fim Lógica de Verificação ---


  // NEW: Extracted core bulk send logic
  const executeBulkSend = useCallback(async (leadsData: Result[]) => {
    setIsSending(true);

    try {
      // 1. Buscar a configuração do Agente de Disparo
      const agentConfig = await fetchAgentConfig('bulk_send');

      if (!agentConfig) {
        toast.error(t('Por favor, configure a IA de Disparo de Mensagens antes de enviar.'));
        return;
      }

      // 2. Mapear os resultados selecionados para o formato de LeadInsertData
      const leadsToInsert = leadsData.map(lead => mapResultToLeadInsert(lead, searchContext?.countryCode));

      // 3. Iniciar o envio em massa. A Edge Function fará o upsert e marcará como prospectado.
      const response = await startBulkSend({
        leadsData: leadsToInsert,
        intervalConfig: intervalConfig,
        agentConfig: agentConfig,
        countryCode: searchContext?.countryCode, // Pass country code for DDI verification
      });

      toast.success(response.message || t('Envio em massa iniciado com sucesso!'));

      // 4. Atualizar o estado local para refletir a mudança de 'prospected'
      const sentIds = leadsData.map(l => l.id);
      updateSearchState({
        results: currentChunkResults.map(lead =>
          sentIds.includes(lead.id) ? { ...lead, prospected: true } : lead
        ),
      });

      setSelectedResults([]);

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : t('Erro ao iniciar o envio em massa.');
      toast.error(errorMessage);
    } finally {
      setIsSending(false);
    }
  }, [currentChunkResults, intervalConfig, t, updateSearchState, searchContext?.countryCode]);


  const handleStartBulkSend = async () => {
    if (selectedResults.length === 0) {
      toast.error(t('Selecione pelo menos um lead para iniciar o envio em massa.'));
      return;
    }
    // Filtrar e mapear os leads selecionados
    const leadsData = currentChunkResults
      .filter(result => selectedResults.includes(result.id))
      .map(result => mapResultToLeadInsert(result, searchContext?.countryCode));
    // VALIDAÇÃO DE WHATSAPP STATUS
    const validationResult = validateLeadsForBulkSend(leadsData);
    if (validationResult.blockedCount > 0) {
      setValidationInfo({
        pendingCount: validationResult.pendingCount,
        invalidCount: validationResult.invalidCount,
        validCount: validationResult.validLeads.length,
      });
      setIsValidationWarningModalOpen(true);
      return;
    }
    // Check de leads já prospectados
    const alreadyProspectedLeads = currentChunkResults
      .filter(result => selectedResults.includes(result.id))
      .filter(lead => lead.prospected);
    if (alreadyProspectedLeads.length > 0) {
      setProspectedLeadsCount(alreadyProspectedLeads.length);
      setIsProspectedWarningModalOpen(true);
      return;
    }
    // Se passou por todas as validações, enviar
    await executeBulkSend(currentChunkResults.filter(result => selectedResults.includes(result.id)));
  };

  const handleConfirmProspectedSend = async (sendAll: boolean) => {
    setIsProspectedWarningModalOpen(false);
    let leadsToSend = currentChunkResults
      .filter(result => selectedResults.includes(result.id));
    if (!sendAll) {
      leadsToSend = leadsToSend.filter(lead => !lead.prospected);
      const newSelectedIds = leadsToSend.map(l => l.id);
      setSelectedResults(newSelectedIds);
    }
    if (leadsToSend.length === 0) {
      toast.error(t('Nenhum lead válido para envio após a filtragem.'));
      return;
    }
    // VALIDAÇÃO DE WHATSAPP STATUS
    const leadsToValidate = leadsToSend.map(lead => mapResultToLeadInsert(lead, searchContext?.countryCode));
    const validationResult = validateLeadsForBulkSend(leadsToValidate);
    if (validationResult.blockedCount > 0) {
      setValidationInfo({
        pendingCount: validationResult.pendingCount,
        invalidCount: validationResult.invalidCount,
        validCount: validationResult.validLeads.length,
      });
      setIsValidationWarningModalOpen(true);
      return;
    }
    await executeBulkSend(leadsToSend);
  };

  const handleConfirmValidationSend = async () => {
    setIsValidationWarningModalOpen(false);
    const leadsToSend = currentChunkResults
      .filter(result => selectedResults.includes(result.id))
      .filter(result => result.status === 'Validado');
    if (leadsToSend.length === 0) {
      toast.error(t('Nenhum lead com número validado para envio.'));
      return;
    }
    await executeBulkSend(leadsToSend);
  };


  const showResults = searchContext !== null || currentChunkResults.length > 0 || isLoading;

  // Total de resultados no chunk atual (para a paginação do frontend)
  const totalResultsForPagination = filteredChunkResults.length;


  return (
    <>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
        className="grid grid-cols-1 lg:grid-cols-12 gap-8 max-w-7xl mx-auto p-6 lg:p-10 pt-8"
      >
        {/* Coluna principal: 9/12 */}
        <div className="lg:col-span-9 flex flex-col gap-6">
          <PageHeading />
          <SearchSection onSearch={handleSearch} isLoading={isLoading} />

          {isLoading && searchContext ? (
            <motion.div
              className="flex items-center justify-center h-64 rounded-xl bg-white dark:bg-gray-800/30 border border-gray-200 dark:border-gray-700/50 card"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              <span className="material-symbols-outlined text-4xl text-primary animate-spin">sync</span>
              <p className="ml-4 text-lg text-gray-600 dark:text-gray-400">{t('dashboard.loading')}</p>
            </motion.div>
          ) : showResults ? (
            <ResultsSection
              results={searchResults} // Paginated view
              filteredChunkResults={filteredChunkResults} // Passando o chunk filtrado
              selectedResults={selectedResults}
              setSelectedResults={setSelectedResults}
              onDeleteResults={handleDeleteResults} // Exclusão em massa
              onDeleteResult={handleDeleteIndividualResult} // Exclusão individual
              onSaveSelectedResults={handleSaveSelectedResults}
              onVerifySelectedNumbers={handleVerifySelectedNumbers} // Passando o novo handler
              isVerifying={isVerifying} // Passando o novo estado
              isWhatsappConnected={isWhatsappConnected} // Passando o estado de conexão

              // Paginação
              currentPage={displayPage} // Página atual dentro do chunk
              totalResults={totalResultsForPagination} // Total de resultados no chunk FILTRADO
              resultsPerPage={resultsPerPage}
              onPageChange={handlePageChange}
              hasMoreChunks={hasMoreChunks}

              // NEW: Passando a função de atualização de controles
              onUpdateControls={(newControls) => {
                if (searchContext) {
                  // Atualiza o contexto com os novos controles, forçando a re-renderização e re-filtragem
                  updateSearchState({ context: { ...searchContext, controls: newControls } });
                }
              }}
              currentControls={searchContext?.controls || { rating: 0, whatsappStatus: 'all', sortBy: 'relevance', resultsPerPage: 20 }}
            />
          ) : (
            <div className="text-center p-10 border border-dashed border-gray-300 dark:border-gray-700 rounded-xl bg-gray-50 dark:bg-gray-800/50">
              <p className="text-lg text-gray-500 dark:text-gray-400">{t('dashboard.no_results_title')}</p>
              <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">{t('dashboard.no_results_subtitle')}</p>
            </div>
          )}
        </div>

        {/* Sidebar: 3/12 */}
        <div className="lg:col-span-3 flex flex-col gap-6">
          <Sidebar
            selectedLeadsCount={selectedResults.length}
            intervalConfig={intervalConfig}
            onSaveIntervals={updateIntervalConfig}
            onSendSelected={handleStartBulkSend}
            isSending={isSending}
          />
        </div>
      </motion.div>

      {/* Modal de Confirmação de Nova Busca */}
      <ConfirmationModal
        isOpen={isNewSearchModalOpen}
        onClose={() => setIsNewSearchModalOpen(false)}
        onConfirm={handleConfirmNewSearch}
        title={t('modals.confirm_new_search_title')}
        message={t('modals.confirm_new_search_message')}
        confirmText={t('modals.confirm_new_search_button')}
        cancelText={t('modals.cancel')}
      />

      {/* Modal de Validação de Números */}
      <ConfirmationModal
        isOpen={isValidationWarningModalOpen}
        onClose={() => setIsValidationWarningModalOpen(false)}
        onConfirm={handleConfirmValidationSend}
        title={t("Atenção: Números Não Verificados")}
        message={`${validationInfo.pendingCount + validationInfo.invalidCount} número(s) serão bloqueados:\n\n• ${validationInfo.pendingCount} Pendente(s)\n• ${validationInfo.invalidCount} Inválido(s)\n\nApenas ${validationInfo.validCount} número(s) validado(s) receberão mensagens.`}
        confirmText={t("Entendi, Enviar Apenas Validados")}
        cancelText={t("Cancelar")}
        tipMessage={t("Valide os números antes de enviar para evitar bloqueio do WhatsApp.")}
      />

      {/* Modal de Aviso de Leads Prospectados */}
      <ConfirmationModal
        isOpen={isProspectedWarningModalOpen}
        onClose={() => setIsProspectedWarningModalOpen(false)}
        onConfirm={() => handleConfirmProspectedSend(true)} // Confirma: Enviar para todos (incluindo prospectados)
        title={t('modals.confirm_resend_title')}
        message={t('modals.confirm_resend_message', { count: prospectedLeadsCount })}
        confirmText={t('Sim, Enviar para Todos')}
        cancelText={t('Não, Enviar Apenas Novos')}
        onCancel={() => handleConfirmProspectedSend(false)} // Cancelar: Enviar apenas para novos
        tipMessage={t('Dica: Se você deseja desmarcar os leads prospectados, clique em "Não, Enviar Apenas Novos" e a seleção será ajustada automaticamente.')}
      />
    </>
  );
};

export default Dashboard;